// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack Context Help — HelpBubble Component
// apps/web/src/components/engine/HelpBubble.tsx
//
// Wraps any element with a context help indicator.
// When context help is enabled:
//   - A small "?" dot appears at the top-right of the element
//   - On hover (desktop) or tap (mobile), a glass tooltip shows
//     the help title and body from the ContextHelpProvider
// When disabled: completely invisible, zero overhead.
//
// Usage:
//   <HelpBubble helpKey="dashboard.compression_ratio">
//     <NeonValue>12.4:1</NeonValue>
//   </HelpBubble>
// ═══════════════════════════════════════════════════════════════════════════

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from './tokens';
import { useContextHelp } from './ContextHelpProvider';

// ─── Types ───────────────────────────────────────────────────────────────────

interface HelpBubbleProps {
  /** The help key matching a ContextHelpItem, e.g. "dashboard.compression_ratio" */
  helpKey: string;
  /** The element to wrap */
  children: ReactNode;
  /** Position of the tooltip relative to the element */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Override the indicator position */
  indicatorPosition?: 'top-right' | 'top-left' | 'inline-end';
  /** Additional style on the wrapper */
  style?: CSSProperties;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const HELP_ACCENT = C.cyan;
const HELP_GLOW = C.cyanGlow;
const TOOLTIP_MAX_WIDTH = 360;
const INDICATOR_SIZE = 14;

// ─── Component ───────────────────────────────────────────────────────────────

export function HelpBubble({
  helpKey,
  children,
  position = 'top',
  indicatorPosition = 'top-right',
  style,
}: HelpBubbleProps) {
  const { enabled, getHelp, getRelated } = useContextHelp();

  // Don't render anything extra when disabled
  if (!enabled) {
    return <>{children}</>;
  }

  const helpItem = getHelp(helpKey);

  // If no help content exists for this key, just render children
  if (!helpItem) {
    return <>{children}</>;
  }

  const relatedItems = getRelated(helpKey);

  return (
    <HelpBubbleInner
      helpItem={helpItem}
      relatedItems={relatedItems}
      position={position}
      indicatorPosition={indicatorPosition}
      style={style}
    >
      {children}
    </HelpBubbleInner>
  );
}

// ─── Inner (only renders when enabled + help exists) ─────────────────────────

function HelpBubbleInner({
  helpItem,
  relatedItems,
  position,
  indicatorPosition,
  style,
  children,
}: {
  helpItem: { title: string; body: string; category: string; learnMoreUrl: string | null };
  relatedItems: { helpKey: string; title: string }[];
  position: 'top' | 'bottom' | 'left' | 'right';
  indicatorPosition: 'top-right' | 'top-left' | 'inline-end';
  style?: CSSProperties;
  children: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0, pos: position });
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x: number;
    let y: number;
    let resolvedPos = position;

    // Calculate initial position
    switch (position) {
      case 'top':
        x = rect.left + rect.width / 2;
        y = rect.top - 8;
        // Flip to bottom if not enough space above
        if (y < 120) { resolvedPos = 'bottom'; y = rect.bottom + 8; }
        break;
      case 'bottom':
        x = rect.left + rect.width / 2;
        y = rect.bottom + 8;
        if (y > vh - 120) { resolvedPos = 'top'; y = rect.top - 8; }
        break;
      case 'left':
        x = rect.left - 8;
        y = rect.top + rect.height / 2;
        if (x < TOOLTIP_MAX_WIDTH + 20) { resolvedPos = 'right'; x = rect.right + 8; }
        break;
      case 'right':
        x = rect.right + 8;
        y = rect.top + rect.height / 2;
        if (x + TOOLTIP_MAX_WIDTH > vw) { resolvedPos = 'left'; x = rect.left - 8; }
        break;
    }

    // Keep tooltip horizontally within viewport
    if (resolvedPos === 'top' || resolvedPos === 'bottom') {
      x = Math.max(TOOLTIP_MAX_WIDTH / 2 + 12, Math.min(x, vw - TOOLTIP_MAX_WIDTH / 2 - 12));
    }

    setCoords({ x, y, pos: resolvedPos });
  }, [position]);

  const handleEnter = useCallback(() => {
    clearTimeout(hideTimeout.current);
    calculatePosition();
    setShow(true);
  }, [calculatePosition]);

  const handleLeave = useCallback(() => {
    hideTimeout.current = setTimeout(() => setShow(false), 150);
  }, []);

  const handleTooltipEnter = useCallback(() => {
    clearTimeout(hideTimeout.current);
  }, []);

  const handleTooltipLeave = useCallback(() => {
    hideTimeout.current = setTimeout(() => setShow(false), 100);
  }, []);

  // Mobile: tap indicator to toggle, tap elsewhere to close
  const handleIndicatorClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (show) {
      setShow(false);
    } else {
      calculatePosition();
      setShow(true);
    }
  }, [show, calculatePosition]);

  // Clean up timeout on unmount
  useEffect(() => () => clearTimeout(hideTimeout.current), []);

  // Category icon
  const categoryIcon: Record<string, string> = {
    metric: '📊',
    navigation: '🧭',
    action: '⚡',
    concept: '💡',
    field: '📝',
    status: '🔵',
  };

  const getTransform = () => {
    switch (coords.pos) {
      case 'top': return 'translate(-50%, -100%)';
      case 'bottom': return 'translate(-50%, 0)';
      case 'left': return 'translate(-100%, -50%)';
      case 'right': return 'translate(0, -50%)';
    }
  };

  // Indicator positioning
  const indicatorStyle: CSSProperties = indicatorPosition === 'inline-end'
    ? {
        position: 'relative' as const,
        display: 'inline-flex',
        marginLeft: 6,
        verticalAlign: 'middle',
        flexShrink: 0,
      }
    : {
        position: 'absolute' as const,
        top: -4,
        ...(indicatorPosition === 'top-right' ? { right: -4 } : { left: -4 }),
        zIndex: 10,
      };

  return (
    <div
      ref={triggerRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{
        position: 'relative',
        display: indicatorPosition === 'inline-end' ? 'inline-flex' : 'relative',
        alignItems: indicatorPosition === 'inline-end' ? 'center' : undefined,
        ...style,
      }}
    >
      {children}

      {/* ── Indicator dot ── */}
      <div
        onClick={handleIndicatorClick}
        style={{
          ...indicatorStyle,
          width: INDICATOR_SIZE,
          height: INDICATOR_SIZE,
          borderRadius: '50%',
          background: `${HELP_ACCENT}18`,
          border: `1px solid ${HELP_ACCENT}40`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: show ? `0 0 8px ${HELP_GLOW}` : 'none',
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget;
          el.style.background = `${HELP_ACCENT}30`;
          el.style.borderColor = `${HELP_ACCENT}70`;
          el.style.boxShadow = `0 0 10px ${HELP_GLOW}`;
          el.style.transform = 'scale(1.15)';
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget;
          el.style.background = `${HELP_ACCENT}18`;
          el.style.borderColor = `${HELP_ACCENT}40`;
          el.style.boxShadow = show ? `0 0 8px ${HELP_GLOW}` : 'none';
          el.style.transform = 'scale(1)';
        }}
      >
        <span style={{
          fontSize: 8,
          fontWeight: 800,
          fontFamily: F.mono,
          color: HELP_ACCENT,
          lineHeight: 1,
          userSelect: 'none',
        }}>
          ?
        </span>
      </div>

      {/* ── Tooltip ── */}
      {show && (
        <div
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
          style={{
            position: 'fixed',
            left: coords.x,
            top: coords.y,
            transform: getTransform(),
            zIndex: 9999,
            width: TOOLTIP_MAX_WIDTH,
            maxWidth: 'calc(100vw - 24px)',
            background: 'rgba(6,12,24,0.97)',
            border: `1px solid ${HELP_ACCENT}25`,
            borderRadius: 12,
            padding: 0,
            fontFamily: F.display,
            color: C.textSoft,
            animation: 'engine-tooltip-in 0.22s cubic-bezier(0.16,1,0.3,1)',
            backdropFilter: 'blur(16px)',
            boxShadow: `
              0 0 1px ${HELP_ACCENT}30,
              0 4px 24px rgba(0,0,0,0.6),
              inset 0 1px 0 ${HELP_ACCENT}08
            `,
            overflow: 'hidden',
          }}
        >
          {/* ── Header ── */}
          <div style={{
            padding: '12px 16px 10px',
            borderBottom: `1px solid ${HELP_ACCENT}12`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: 14 }}>
              {categoryIcon[helpItem.category] || '💡'}
            </span>
            <span style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.text,
              letterSpacing: '-0.01em',
              flex: 1,
            }}>
              {helpItem.title}
            </span>
            <span style={{
              fontSize: 8,
              fontFamily: F.mono,
              color: `${HELP_ACCENT}80`,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '2px 6px',
              background: `${HELP_ACCENT}10`,
              borderRadius: 4,
              border: `1px solid ${HELP_ACCENT}15`,
            }}>
              {helpItem.category}
            </span>
          </div>

          {/* ── Body ── */}
          <div style={{
            padding: '12px 16px',
            fontSize: 12,
            lineHeight: 1.65,
            color: C.textSoft,
          }}>
            {renderBody(helpItem.body)}
          </div>

          {/* ── Related (if any) ── */}
          {relatedItems.length > 0 && (
            <div style={{
              padding: '8px 16px 10px',
              borderTop: `1px solid ${HELP_ACCENT}08`,
            }}>
              <div style={{
                fontSize: 8,
                fontFamily: F.mono,
                color: C.dim,
                letterSpacing: '0.1em',
                marginBottom: 6,
                textTransform: 'uppercase',
              }}>
                RELATED
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {relatedItems.slice(0, 4).map(r => (
                  <span
                    key={r.helpKey}
                    style={{
                      fontSize: 10,
                      fontFamily: F.mono,
                      color: `${HELP_ACCENT}90`,
                      padding: '2px 8px',
                      background: `${HELP_ACCENT}08`,
                      borderRadius: 4,
                      border: `1px solid ${HELP_ACCENT}12`,
                    }}
                  >
                    {r.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Learn More ── */}
          {helpItem.learnMoreUrl && (
            <div style={{
              padding: '6px 16px 10px',
              borderTop: `1px solid ${HELP_ACCENT}08`,
            }}>
              <a
                href={helpItem.learnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 10,
                  fontFamily: F.mono,
                  color: HELP_ACCENT,
                  textDecoration: 'none',
                  letterSpacing: '0.04em',
                }}
                onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
              >
                LEARN MORE →
              </a>
            </div>
          )}

          {/* ── Scanline accent ── */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${HELP_ACCENT}40, transparent)`,
          }} />
        </div>
      )}
    </div>
  );
}

// ─── Body renderer (supports **bold** markers) ───────────────────────────────

function renderBody(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} style={{ color: C.text, fontWeight: 600 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

// ─── Quick toggle button (for top bar or sidebar) ────────────────────────────

export function ContextHelpToggle({ compact = false }: { compact?: boolean }) {
  const { enabled, toggle, loading } = useContextHelp();

  return (
    <button
      onClick={toggle}
      title={enabled ? 'Disable context help' : 'Enable context help'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 0 : 6,
        padding: compact ? '6px 8px' : '6px 14px',
        background: enabled ? `${HELP_ACCENT}15` : 'transparent',
        border: `1px solid ${enabled ? `${HELP_ACCENT}40` : C.faint}`,
        borderRadius: 8,
        color: enabled ? HELP_ACCENT : C.muted,
        fontFamily: F.mono,
        fontSize: 10,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        letterSpacing: '0.06em',
        fontWeight: 600,
        opacity: loading ? 0.6 : 1,
      }}
      onMouseEnter={e => {
        if (!enabled) {
          e.currentTarget.style.borderColor = `${HELP_ACCENT}30`;
          e.currentTarget.style.color = `${HELP_ACCENT}80`;
        }
      }}
      onMouseLeave={e => {
        if (!enabled) {
          e.currentTarget.style.borderColor = C.faint;
          e.currentTarget.style.color = C.muted;
        }
      }}
    >
      <span style={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: enabled ? `${HELP_ACCENT}25` : `${C.dim}20`,
        border: `1.5px solid ${enabled ? HELP_ACCENT : C.dim}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 9,
        fontWeight: 800,
        lineHeight: 1,
        transition: 'all 0.2s ease',
      }}>
        ?
      </span>
      {!compact && (enabled ? 'HELP ON' : 'HELP')}
    </button>
  );
}

export default HelpBubble;
